'use client'

import { type StatsigClient } from '@statsig/js-client'
import { createContext, JSX, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { statsigDevMock } from './StatsigDevMock'

interface StatsigContextType {
  statsigClient: StatsigClient | null
}

interface StatsigProviderProps {
  children: ReactNode
}

declare global {
  var __statsigClient__: StatsigClient | null | undefined
}

// Get the global Statsig client if it exists
// it will be set when the simulation is embeded in the WEB
export const getGlobalClient = () => {
  // In development mode, use the mock immediately
  if (!import.meta.env.VITE_VERCEL_ENV || import.meta.env.VITE_VERCEL_ENV === 'development') {
    console.log('[StatsigProvider] Using development mock')
    return statsigDevMock as StatsigClient
  }

  return globalThis.__statsigClient__
}

const StatsigContext = createContext<StatsigContextType | undefined>(undefined)

export function StatsigProvider({ children }: StatsigProviderProps): JSX.Element {
  const [statsigClient, setStatsigClient] = useState<StatsigClient | null>(null)
  const value: StatsigContextType = useMemo(
    () => ({
      statsigClient,
    }),
    [statsigClient]
  )

  useEffect(() => {
    // In production, wait for the global client
    if (!statsigClient) {
      // try to get the global client every 100ms for 5 seconds
      // if not found, leave statsigClient as null
      let attempts = 0
      const maxAttempts = 50

      const interval = setInterval(() => {
        attempts = attempts + 1
        const globalClient = getGlobalClient()
        if (globalClient) {
          setStatsigClient(globalClient)
          clearInterval(interval)
        } else if (attempts >= maxAttempts) {
          clearInterval(interval)
        }
      }, 100)

      return () => clearInterval(interval)
    }

    return () => {
      setStatsigClient(null)
    }
  }, [])

  return <StatsigContext.Provider value={value}>{children}</StatsigContext.Provider>
}

export function useStatsigClient(): StatsigClient | null {
  const context = useContext(StatsigContext)
  if (!context) {
    throw new Error('useStatsig must be used within a StatsigProvider')
  }
  return context.statsigClient
}
